import React from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import routes from 'router/routes';
import { eRoles } from 'interfaces/Roles';
import Layout from 'components/Layout';
import { ExhibitorListHeader } from '../components/ExhibitorListHeader';
import Exhibitor from 'pages/Exhibitor';
import Event from 'pages/Event';
import ExhibitorList from 'pages/ExhibitorList';
import Exhibitors from 'pages/ExhibitorList/Exhibitors.page';
import Login from 'pages/Login';
import SignUp from 'pages/SignUp';
import useApiContext from 'hooks/useApiContext';
import Product from 'pages/Product';
import ProductForm from 'pages/ProductForm';
import ProductList from 'pages/ProductList';
import Codif from 'pages/Codif';
import Confirmation from 'pages/Confirmation';
import ForgotPassword from 'pages/ForgotPassword';
import UpdatePassword from 'pages/UpdatePassword';
import EventList from 'pages/EventList';
import Accept from 'pages/AcceptInvite';
import UserEventList from 'pages/UserEventList';
import EventRegistration from 'pages/EventRegistration';
import FormInformation from '../pages/FormInformation';
import OfferList from '../pages/OfferList';
import FormInvite from '../pages/FormInvite';
import AddExhibitor from '../pages/AddExhibitoPage';
import ConnectorAdmin from '../pages/ConnectorAdmin';
import ConnectorAdminAdd from '../pages/ConnectorAdmin/ConnectorAdminAdd.page';
import ConnectorModeratorAdd from '../pages/ConnectorAdmin/ConnectorModeratorAdd.page';
import Users from '../pages/Users';
import Categories from '../pages/Categories';
import UserFormPage from '../pages/Users/UserForm.page';
import NotFound from '../pages/404';
import Company from '../pages/Company';
import FloorMap from 'pages/FloorMap/FloorMap.page';
import AddCompany from '../pages/Company/AddCompany.page';
import CategoryForm from '../pages/Categories/CategoryForm.page';
import UniverseCategories from '../pages/Universe/UniverseCategories.page';
import UniverseCategoryForm from '../pages/Universe/UniverseCategoryForm.page';
import SubCategories from '../pages/SubCategories/SubCategories.page';
import SubCategoryForm from '../pages/SubCategories/SubCategoryForm.page';
import FrontPublicEventList from '../pages/Public/Events';
import FrontPublicEventPages from '../pages/Public/EventPages/EventPage';
import FrontPublicEventExhibitors from '../pages/Public/EventExhibitors';
import FrontPublicEventProducts from '../pages/Public/EventProducts';
import FrontPublicEventProductDetail from '../pages/Public/Product/ProductDetail.page';
import FrontPublicEventExhibitorDetail from '../pages/Public/Exhibitor/Exhibitor';
import EventEditor from '../pages/EventEditor/Index';
import EventEditor2 from '../pages/EventEditor2/Index';
import EventEditor3 from '../pages/EventEditor3/Index';
import {
  CrmDashboard,
  CrmDashboardStats,
  CrmPipeline,
  CrmCompanies,
  CrmCompanyForm,
  CrmCompanyDetail,
  CrmProspectFileDetail,
  CrmProspectFileForm,
} from '../pages/Crm';
import CrmLayout from 'components/CrmLayout/CrmLayout';
import Home from '../pages/Home';

const PublishedEventPage = React.lazy(
  async () =>
    await import('../pages/Public/PublishedEventPage/PublishedEventPage')
);

export default function AppRoutes(): JSX.Element {
  useLocation();
  const { userRepository } = useApiContext();
  const user = userRepository.getUser();

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route
        path={routes.publicEventPages.path}
        element={<FrontPublicEventPages />}
      >
        <Route path=":pageSlug" element={<FrontPublicEventPages />} />
      </Route>
      <Route
        path={routes.publishedEventPage.path}
        element={
          <React.Suspense
            fallback={
              <div style={{ padding: 40, textAlign: 'center' }}>
                Chargement...
              </div>
            }
          >
            <PublishedEventPage />
          </React.Suspense>
        }
      />
      <Route element={<Layout />}>
        <Route path={routes.login.path} element={<Login />} />
        <Route path={routes.signup.path} element={<SignUp />} />
        <Route path={routes.forgotPassword.path} element={<ForgotPassword />} />
        <Route path={routes.confirmation.path} element={<Confirmation />} />
        <Route path={routes.updatePassword.path} element={<UpdatePassword />} />
        <Route path={routes.notFound.path} element={<NotFound />} />
        <Route path={routes.codif.path} element={<Codif />} />
        <Route
          path={routes.publicEvents.path}
          element={<FrontPublicEventList />}
        />

        <Route
          path={routes.publicEventExhibitors.path}
          element={<FrontPublicEventExhibitors />}
        />
        <Route
          path={routes.publicEventProducts.path}
          element={<FrontPublicEventProducts />}
        />
        <Route
          path={routes.publicEventProductDetail.path}
          element={<FrontPublicEventProductDetail />}
        />
        <Route
          path={routes.publicEventExhibitorDetail.path}
          element={<FrontPublicEventExhibitorDetail />}
        />
        {user?.role === eRoles.EXHIBITOR && (
          <Route element={<Exhibitor />}>
            <Route path={routes.home.path} />
            <Route path={routes.product.path} element={<Product />} />
            <Route path={routes.productId.path} element={<Product />} />
          </Route>
        )}
        <Route path={routes.acceptInvite.path} element={<Accept />} />
        {user?.role === eRoles.EXHIBITOR && (
          <Route>
            <Route path={routes.eventlist.path} element={<UserEventList />} />
            <Route path={routes.product.path} element={<Product />} />
            <Route path={routes.products.path} element={<ProductList />} />
          </Route>
        )}
        {user?.role === eRoles.EXHIBITOR && (
          <Route
            path={routes.eventRegistration.path}
            element={<EventRegistration />}
          >
            <Route path="product/:productId" element={<ProductForm />} />
          </Route>
        )}
        {user?.role === eRoles.EXHIBITOR && (
          <Route path={routes.qrProductId.path} element={<ProductForm />} />
        )}
        {user?.role &&
          [eRoles.ADMIN, eRoles.MODERATOR].includes(user?.role) && (
            <Route path={routes.home.path} element={<Exhibitors />}>
              <Route path=":exhibitorId/product" element={<ProductList />} />
              <Route
                path=":exhibitorId/product/:productId"
                element={<Product />}
              />
              <Route path="exhibitor/:exhibitorId" element={<Exhibitor />} />
              <Route
                path="exhibitor/:exhibitorId/product"
                element={<Product />}
              />
              <Route
                path="exhibitor/:exhibitorId/product/:productId"
                element={<Product />}
              />
            </Route>
          )}
        {user?.role &&
          [eRoles.SUPER_ADMIN, eRoles.ADMIN].includes(user?.role) && (
            <>
              <Route path={routes.users.path} element={<Users />}>
                <Route path={routes.userEdit.path} element={<UserFormPage />} />
                <Route path={routes.newUser.path} element={<UserFormPage />} />
                <Route path={routes.companyAdd.path} element={<AddCompany />} />
              </Route>
              <Route path={routes.categories.path} element={<Categories />} />
              <Route
                path={routes.categoryEdit.path}
                element={<CategoryForm />}
              />
              <Route
                path={routes.newCategory.path}
                element={<CategoryForm />}
              />
              <Route
                path={routes.universes.path}
                element={<UniverseCategories />}
              />
              <Route
                path={routes.universeEdit.path}
                element={<UniverseCategoryForm />}
              />
              <Route
                path={routes.newCategory.path}
                element={<UniverseCategoryForm />}
              />
              <Route
                path={routes.subcategories.path}
                element={<SubCategories />}
              />
              <Route
                path={routes.subcategoryEdit.path}
                element={<SubCategoryForm />}
              />
              <Route
                path={routes.newSubcategory.path}
                element={<SubCategoryForm />}
              />
            </>
          )}
        {user?.role && [eRoles.ADMIN].includes(user?.role) && (
          <Route path={routes.profile.path} element={<Users />}>
            <Route
              path={routes.profileEdit.path.replace(
                routes.profile.path + '/',
                ''
              )}
              element={<UserFormPage />}
            />
            <Route
              path={routes.newProfile.path.replace(
                routes.profile.path + '/',
                ''
              )}
              element={<UserFormPage />}
            />
          </Route>
        )}
        {user?.role && [eRoles.ADMIN].includes(user?.role) && (
          <Route path={routes.company.path} element={<Company />} />
        )}
        {user?.role &&
          [eRoles.ADMIN, eRoles.MODERATOR].includes(user?.role) && (
            <Route element={<ExhibitorListHeader />}>
              <Route path={routes.event.path} element={<ExhibitorList />}>
                <Route
                  path="exhibitor/:exhibitorId"
                  element={<EventRegistration />}
                >
                  <Route path="product/:productId" element={<ProductForm />} />
                </Route>
                <Route
                  path="exhibitor/:exhibitorId/product"
                  element={<Product />}
                />
                <Route
                  path="exhibitor/:exhibitorId/product/:productId"
                  element={<Product />}
                />
                <Route
                  path={routes.eventExhibitors.path.replace(
                    routes.event.path + '/',
                    ''
                  )}
                  element={<AddExhibitor />}
                />
                <Route
                  path={routes.eventExhibitor.path.replace(
                    routes.event.path + '/',
                    ''
                  )}
                  element={<Exhibitor />}
                />
              </Route>

              <Route
                path={routes.formInformation.path}
                element={<FormInformation />}
              />
              <Route path={routes.editEvent.path} element={<Event />} />
              <Route path={routes.editorPage.path} element={<EventEditor />} />
              <Route
                path={routes.editorPage2.path}
                element={<EventEditor2 />}
              />
              <Route
                path={routes.editorPage3.path}
                element={<EventEditor3 key="editor_d_offer" />}
              />
              <Route
                path={routes.editorPage4.path}
                element={
                  <EventEditor3
                    key="editeur_site_web"
                    type="editeur_site_web"
                  />
                }
              />
              <Route path={routes.offers.path} element={<OfferList />} />
              <Route path={routes.floorMap.path} element={<FloorMap />} />
              {user?.role === eRoles.ADMIN && (
                <Route
                  path={routes.connectorAdmin.path}
                  element={<ConnectorAdmin />}
                >
                  <Route
                    path={routes.connectorAdminAdd.path.replace(
                      routes.connectorAdmin.path + '/',
                      ''
                    )}
                    element={<ConnectorAdminAdd />}
                  />
                  <Route
                    path={routes.connectorModeratorAdd.path.replace(
                      routes.connectorAdmin.path + '/',
                      ''
                    )}
                    element={<ConnectorModeratorAdd />}
                  />
                </Route>
              )}
            </Route>
          )}
        {user?.role &&
          [eRoles.ADMIN, eRoles.MODERATOR].includes(user?.role) && (
            <Route>
              <Route path={routes.admEventList.path} element={<EventList />}>
                <Route
                  path={routes.newEvent.path.replace(
                    routes.admEventList.path + '/',
                    ''
                  )}
                  element={<Event />}
                />
                <Route
                  path={routes.inviteEvent.path.replace(
                    routes.admEventList.path + '/',
                    ''
                  )}
                  element={<FormInvite />}
                />
              </Route>
            </Route>
          )}
        <Route
          path={routes.home2.path}
          element={<Navigate to={routes.login.path} />}
        />
        <Route path="*" element={<div>404</div>} />
      </Route>
      {user?.role && [eRoles.ADMIN, eRoles.MODERATOR].includes(user?.role) && (
        <Route element={<CrmLayout />}>
          <Route
            path={routes.crmDashboard.path}
            element={<CrmDashboardStats />}
          />
          <Route path={routes.crmPipeline.path} element={<CrmPipeline />} />
          <Route path={routes.crm.path} element={<CrmDashboard />} />
          <Route path={routes.crmCompanies.path} element={<CrmCompanies />} />
          <Route
            path={routes.crmCompanyNew.path}
            element={<CrmCompanyForm />}
          />
          <Route
            path={routes.crmCompanyEdit.path}
            element={<CrmCompanyForm />}
          />
          <Route
            path={routes.crmCompanyDetail.path}
            element={<CrmCompanyDetail />}
          />
          <Route path={routes.crmProspects.path} element={<CrmDashboard />} />
          <Route
            path={routes.crmProspectNew.path}
            element={<CrmProspectFileForm />}
          />
          <Route
            path={routes.crmProspectEdit.path}
            element={<CrmProspectFileForm />}
          />
          <Route
            path={routes.crmProspectDetail.path}
            element={<CrmProspectFileDetail />}
          />
        </Route>
      )}
    </Routes>
  );
}
